import React, { useMemo, useState } from 'react'
import { FunnelIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import PedidoOfimaDetailModal from '../components/erp/PedidosOfima/PedidoOfimaDetailModal'
import PedidosOfimaTable from '../components/erp/PedidosOfima/PedidosOfimaTable'
import Button from '../components/ui/Button/Button'
import { FilterPanel } from '../components/ui'
import PaginationControls from '../components/ui/Pagination/PaginationControls'
import { useOfimaPedidos } from '../hooks/useOfimaPedidos'
import { usePedidosOfimaClientFilters } from '../hooks/usePedidosOfimaClientFilters'
import { OFIMA_PEDIDOS_LIMIT_OPTIONS, filterOfimaPedidos, normalizeOfimaTipoDcto } from '../utils/ofimaPedidos'

const PedidosOfimaPage: React.FC = () => {
  const {
    showFilters,
    setShowFilters,
    clientFilters,
    hasClientFilters,
    activeFiltersCount,
    filterSections,
    clearFilters
  } = usePedidosOfimaClientFilters()
  const tipodcto = normalizeOfimaTipoDcto(clientFilters.tiposDocumento)
  const {
    page,
    setPage,
    limit,
    changeLimit,
    pedidos,
    pagination,
    totalPages,
    isLoading,
    error,
    orden,
    searchOrden
  } = useOfimaPedidos(tipodcto)

  const [ordenInput, setOrdenInput] = useState('')
  const [selectedOrden, setSelectedOrden] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  const filteredPedidos = useMemo(
    () => filterOfimaPedidos(pedidos, clientFilters),
    [clientFilters, pedidos]
  )

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault()
    searchOrden(ordenInput)
  }

  const clearSearch = () => {
    setOrdenInput('')
    if (orden) searchOrden('')
  }

  const clearInput = () => {
    if (ordenInput.trim() === orden) {
      clearSearch()
      return
    }
    setOrdenInput('')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto w-full max-w-full px-3 py-6 sm:px-4 lg:px-6">
        <div className="mb-4">
          <h2>Pedidos Ofima</h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Remisiones, facturas y notas crédito.
          </p>
        </div>

        <div className="mb-4 flex flex-col gap-3 rounded-lg bg-white p-4 shadow-sm lg:flex-row lg:items-end lg:justify-between">
          <form onSubmit={handleSearch} className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-[240px] flex-1">
              <label htmlFor="ofima-orden" className="mb-1 block text-sm font-medium text-gray-700">
                Pedido
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  id="ofima-orden"
                  value={ordenInput}
                  onChange={(event) => setOrdenInput(event.target.value)}
                  maxLength={20}
                  placeholder="Número de pedido"
                  aria-label="Número de pedido"
                  className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-9 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {ordenInput && (
                  <button
                    type="button"
                    onClick={clearInput}
                    className="absolute right-2 top-2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Limpiar búsqueda"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <Button type="submit" disabled={isLoading}>
              Buscar
            </Button>
          </form>
          <div className="relative overflow-visible self-end">
            <Button
              type="button"
              variant="secondary"
              leftIcon={FunnelIcon}
              className="relative"
              onClick={(event) => {
                event.stopPropagation()
                setShowFilters((current) => !current)
              }}
              onMouseDown={(event) => {
                event.stopPropagation()
              }}
              aria-expanded={showFilters}
              aria-label="Abrir filtros"
            >
              Filtros
              {activeFiltersCount > 0 && (
                <span className="absolute -right-1 -top-1 flex min-w-[20px] items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-bold text-white">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
            <FilterPanel
              isOpen={showFilters}
              onClose={() => setShowFilters(false)}
              sections={filterSections}
              onClearAll={clearFilters}
              activeFiltersCount={activeFiltersCount}
              position="right"
            />
          </div>
        </div>

        {orden && (
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-500">Resultado de</span>
            <button
              type="button"
              onClick={clearSearch}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-800 hover:bg-blue-100"
            >
              Pedido {orden}
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Quitar búsqueda</span>
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
            {error}
          </div>
        )}

        <div className="mb-6 overflow-hidden rounded-lg bg-white shadow-sm">
          <PedidosOfimaTable
            pedidos={filteredPedidos}
            loadedCount={pedidos.length}
            isLoading={isLoading}
            hasClientFilters={hasClientFilters}
            onView={(nextOrden) => {
              setSelectedOrden(nextOrden)
              setDetailOpen(true)
            }}
          />

          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-700" htmlFor="ofima-limit">
              Mostrar
              <select
                id="ofima-limit"
                value={limit}
                disabled={isLoading}
                onChange={(event) => changeLimit(Number(event.target.value))}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-8 text-sm disabled:opacity-50"
              >
                {OFIMA_PEDIDOS_LIMIT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option} por página
                  </option>
                ))}
              </select>
            </label>
            <p className="text-sm text-gray-600">
              {filteredPedidos.length} de {pedidos.length} en esta página
              {pagination ? ` · ${pagination.total} pedidos` : ''}
              {hasClientFilters ? ' · con filtros' : ''}
            </p>
            {totalPages > 1 && (
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>

      <PedidoOfimaDetailModal
        orden={selectedOrden}
        isOpen={detailOpen}
        tipodcto={tipodcto}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  )
}

export default PedidosOfimaPage
